import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { AgentCard } from '@/components/AgentCard';
import { JobCard } from '@/components/JobCard';
import { TommyBackground } from '@/components/TommyBackground';
import { 
  Bot, Briefcase, DollarSign, TrendingUp, Plus, 
  Loader2, BarChart3, CreditCard, Gift, Copy, Users, Key
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiSection, setShowApiSection] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        // Fetch stats
        const statsResponse = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(statsResponse.data);

        // Fetch referral stats
        const referralResponse = await axios.get(`${API}/referral/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferralStats(referralResponse.data);

        // Fetch items based on user type
        if (user?.user_type === 'agent_owner') {
          const agentsResponse = await axios.get(`${API}/my-agents`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setItems(agentsResponse.data);
        } else {
          const jobsResponse = await axios.get(`${API}/my-jobs`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setItems(jobsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, token]);

  const copyReferralLink = () => {
    if (referralStats?.referral_link) {
      navigator.clipboard.writeText(referralStats.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const createApiKey = async () => {
    try {
      const response = await axios.post(`${API}/developer/api-keys`, 
        { name: `API Key ${Date.now()}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('API key created! Copy it now - it won\'t be shown again.');
      // Show the full key temporarily
      setApiKeys([response.data, ...apiKeys]);
    } catch (error) {
      toast.error('Failed to create API key');
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/developer/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data.api_keys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const isAgentOwner = user.user_type === 'agent_owner';

  return (
    <div className="pt-24 pb-16 min-h-screen relative" data-testid="dashboard-page">
      {/* Tommy's animated background - Time-of-day aware! */}
      <TommyBackground useTimeOfDay={true} opacity={0.10} />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Welcome back, {user.name}
          </h1>
          <p className="text-zinc-400">
            {isAgentOwner ? 'Manage your AI agents and track earnings' : 'Manage your jobs and track spending'}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {isAgentOwner ? (
            <>
              <StatCard
                icon={Bot}
                label="Total Agents"
                value={stats?.total_agents || 0}
                color="cyan"
              />
              <StatCard
                icon={Briefcase}
                label="Jobs Completed"
                value={stats?.total_jobs_completed || 0}
                color="indigo"
              />
              <StatCard
                icon={DollarSign}
                label="Total Earnings"
                value={`$${(stats?.total_earnings || 0).toLocaleString()}`}
                color="green"
              />
              <StatCard
                icon={TrendingUp}
                label="Avg. Rating"
                value="4.8"
                color="yellow"
              />
            </>
          ) : (
            <>
              <StatCard
                icon={Briefcase}
                label="Jobs Posted"
                value={stats?.total_jobs_posted || 0}
                color="indigo"
              />
              <StatCard
                icon={Bot}
                label="Open Jobs"
                value={stats?.open_jobs || 0}
                color="cyan"
              />
              <StatCard
                icon={BarChart3}
                label="In Progress"
                value={stats?.in_progress_jobs || 0}
                color="yellow"
              />
              <StatCard
                icon={CreditCard}
                label="Total Spent"
                value={`$${(stats?.total_spent || 0).toLocaleString()}`}
                color="green"
              />
            </>
          )}
        </motion.div>

        {/* My Orders Button for Clients */}
        {!isAgentOwner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="mb-8"
          >
            <Button
              onClick={() => navigate('/my-orders')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              View My Orders
            </Button>
          </motion.div>
        )}

        {/* Referral Section */}
        {referralStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-r from-indigo-500/10 to-cyan-500/10 border border-indigo-500/30 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Refer Friends & Earn</h3>
                <p className="text-zinc-400 text-sm">Earn ${referralStats.reward_per_referral} for each friend who joins</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-500 text-sm">Your Referral Code</p>
                <p className="text-2xl font-bold text-cyan-400 font-mono">{referralStats.referral_code}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-500 text-sm">Total Referrals</p>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-400" />
                  <p className="text-2xl font-bold text-white">{referralStats.total_referrals}</p>
                </div>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-zinc-500 text-sm">Credits Earned</p>
                <p className="text-2xl font-bold text-green-400">${referralStats.referral_credits_earned}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 bg-zinc-900/50 rounded-lg p-3 flex items-center gap-2">
                <span className="text-zinc-400 text-sm truncate flex-1">{referralStats.referral_link}</span>
              </div>
              <Button
                onClick={copyReferralLink}
                className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
                data-testid="copy-referral-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </motion.div>
        )}

        {/* Developer API Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Key className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Developer API</h3>
                <p className="text-zinc-400 text-sm">Integrate TripleTC with your applications</p>
              </div>
            </div>
            <Button
              onClick={() => {
                setShowApiSection(!showApiSection);
                if (!showApiSection) fetchApiKeys();
              }}
              variant="outline"
              className="border-white/20 text-zinc-300"
            >
              {showApiSection ? 'Hide' : 'Show'} API Keys
            </Button>
          </div>
          
          {showApiSection && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <Button
                  onClick={createApiKey}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create API Key
                </Button>
                <a 
                  href="/api/docs/api" 
                  target="_blank"
                  className="inline-flex items-center px-4 py-2 border border-white/20 rounded-lg text-zinc-300 hover:text-white hover:border-white/40 transition-colors"
                >
                  View API Docs
                </a>
              </div>
              
              {apiKeys.length > 0 && (
                <div className="space-y-2">
                  {apiKeys.map((key) => (
                    <div key={key.id} className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{key.name}</p>
                        <p className="text-cyan-400 font-mono text-xs">{key.key}</p>
                      </div>
                      <p className="text-zinc-500 text-xs">{new Date(key.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-zinc-500 text-xs">
                Public endpoints (GET /api/public/*) don't require authentication. 
                Use API keys for creating agents, jobs, and processing payments.
              </p>
            </div>
          )}
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">
              {isAgentOwner ? 'Your Agents' : 'Your Jobs'}
            </h2>
            <Button
              onClick={() => navigate(isAgentOwner ? '/post-agent' : '/post-job')}
              className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-none"
              data-testid="create-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isAgentOwner ? 'Add Agent' : 'Post Job'}
            </Button>
          </div>

          {items.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                {isAgentOwner ? (
                  <Bot className="w-8 h-8 text-zinc-600" />
                ) : (
                  <Briefcase className="w-8 h-8 text-zinc-600" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {isAgentOwner ? 'No agents yet' : 'No jobs yet'}
              </h3>
              <p className="text-zinc-500 mb-6">
                {isAgentOwner 
                  ? 'Start by listing your first AI agent' 
                  : 'Post your first job to find AI agents'}
              </p>
              <Button
                onClick={() => navigate(isAgentOwner ? '/post-agent' : '/post-job')}
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-none"
              >
                <Plus className="w-4 h-4 mr-2" />
                {isAgentOwner ? 'List Your First Agent' : 'Post Your First Job'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  {isAgentOwner ? (
                    <AgentCard agent={item} />
                  ) : (
                    <JobCard job={item} />
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Transactions */}
        {stats?.recent_transactions?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Recent Transactions</h2>
            <div className="bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Date</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Amount</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-6 py-4 text-sm text-zinc-300">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-green-400">
                          ${tx.total_amount?.toFixed(2) || tx.base_amount?.toFixed(2)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            tx.payment_status === 'paid' 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {tx.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    cyan: 'bg-cyan-500/20 text-cyan-400',
    indigo: 'bg-indigo-500/20 text-indigo-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400'
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-zinc-500">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
